import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { applyCurrencyMeta } from '@wave/shared';
import type {
  Account,
  ArchiveCoverageResponse,
  ArchiveFindingsResponse,
  ArchiveOpsResponse,
  AuditHistoryResponse,
  BalanceAuditResponse,
  BalanceChangesResponse,
  BalanceRow,
  BalancesTimeseries,
  BotTxnsResponse,
  CcyCode,
  ChatBalancesConfigDto,
  ChatBalancesResponse,
  ChatSyncConfigDto,
  Currency,
  DashboardResponse,
  ExecutorChangeEntry,
  FindingKind,
  Operator,
  OrderDetail,
  OrderListItem,
  RateHistoryEntry,
  RateProviderInfo,
  RateSourceConfig,
  TelegramEvent,
  TelegramEventStatus,
  TelegramHandlerInfo,
  TelegramListeningChat,
  TelegramMessageLogEntry,
  RuleTemplate,
  TelegramBotConfigDto,
  TelegramBotStatusDto,
  TelegramChatBalance,
  TelegramRule,
  TransactionRow,
  TronConfigDto,
  TronWalletRow,
  UnmatchedResponse,
  UsdtRate,
} from '@wave/shared';
import { api } from './client';

export const queryKeys = {
  refs: ['ref'] as const,
  currencies: ['ref', 'currencies'] as const,
  accounts: ['ref', 'accounts'] as const,
  operators: ['ref', 'operators'] as const,
  usdtRates: ['ref', 'usdt-rates'] as const,
  ordersList: (status: string, search?: string, limit?: number) =>
    ['orders', 'list', status, search ?? '', limit ?? 'default'] as const,
  order: (id: string) => ['orders', id] as const,
  executorHistory: (id: string) => ['orders', id, 'executor-history'] as const,
  balances: ['balances'] as const,
  balanceAudit: ['balance-audit'] as const,
  archiveFindings: ['archive', 'findings'] as const,
  archiveCoverage: ['archive', 'coverage'] as const,
  archiveOps: (chatId: string, date?: string) => ['archive', 'ops', chatId, date ?? null] as const,
  balanceChanges: ['balance-changes'] as const,
  chatBalances: ['chat-balances'] as const,
  chatBalancesHistory: (range: string) => ['chat-balances', 'history', range] as const,
  chatBalancesHistoryByCcy: (range: string) => ['chat-balances', 'history', 'by-ccy', range] as const,
  chatBalancesHistoryByChat: (range: string) => ['chat-balances', 'history', 'by-chat', range] as const,
  chatBalancesDaily: (days: number) => ['chat-balances', 'history', 'daily', days] as const,
  chatBalancesDayRecon: (date: string | null) => ['chat-balances', 'history', 'day-reconciliation', date ?? 'today'] as const,
  chatBalancesChatDetail: (chatId: string, range: string) =>
    ['chat-balances', 'history', 'chat', chatId, range] as const,
  transactions: (page: number) => ['transactions', page] as const,
  dashboard: ['dashboard'] as const,
  tronConfig: ['tron', 'config'] as const,
  tronWallets: ['tron', 'wallets'] as const,
};

export function useCurrencies() {
  return useQuery({
    queryKey: queryKeys.currencies,
    queryFn: async () => {
      const data = await api.get<Currency[]>('/ref/currencies');
      // Вливаем метаданные (символ/scale/позиция/порядок) в shared-реестр —
      // fmtAmt и сортировки начинают видеть валюты, добавленные через UI.
      applyCurrencyMeta(data);
      return data;
    },
    staleTime: 60 * 60 * 1000,
  });
}

export function useAccounts(kind: 'all' | 'client' | 'executor' = 'all') {
  return useQuery({
    queryKey: [...queryKeys.accounts, kind] as const,
    queryFn: () => api.get<Account[]>('/ref/accounts', { kind }),
    staleTime: 60 * 60 * 1000,
  });
}

export function useOperators() {
  return useQuery({
    queryKey: queryKeys.operators,
    queryFn: () => api.get<Operator[]>('/ref/operators'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useUsdtRates() {
  return useQuery({
    queryKey: queryKeys.usdtRates,
    queryFn: () => api.get<UsdtRate[]>('/ref/usdt-rates'),
    staleTime: 5 * 60 * 1000,
  });
}

export type OrdersListData = { items: OrderListItem[]; total: number };

export function useOrdersList(
  status: 'all' | 'new' | 'progress' | 'closed' | 'reject' | 'deleted',
  search?: string,
  limit?: number,
) {
  const q = search?.trim();
  return useQuery({
    queryKey: queryKeys.ordersList(status, q, limit),
    queryFn: () =>
      api.get<OrdersListData>('/orders', {
        status,
        ...(q ? { search: q } : {}),
        ...(limit ? { limit } : {}),
      }),
  });
}

export function useOrder(id: string | null) {
  return useQuery({
    queryKey: id ? queryKeys.order(id) : ['orders', '__none__'],
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
    enabled: !!id,
  });
}

/** История смены исполнителя заявки (для блока «почему сменился»). */
export function useExecutorHistory(id: string | null) {
  return useQuery({
    queryKey: id ? queryKeys.executorHistory(id) : ['orders', '__none__', 'executor-history'],
    queryFn: () => api.get<ExecutorChangeEntry[]>(`/orders/${id}/executor-history`),
    enabled: !!id,
  });
}

export function useBalances() {
  return useQuery({
    queryKey: queryKeys.balances,
    queryFn: () => api.get<BalanceRow[]>('/balances'),
  });
}

export function useBalanceTimeseries(range: '7d' | '30d' | '90d' | '365d') {
  return useQuery({
    queryKey: ['balances', 'timeseries', range],
    queryFn: () => api.get<BalancesTimeseries>('/balances/timeseries', { range }),
    staleTime: 60_000,
  });
}

/** Реальные остатки по чатам из внешнего API (живой прокси). Страница /balances. */
export function useChatBalances() {
  return useQuery({
    queryKey: queryKeys.chatBalances,
    queryFn: () => api.get<ChatBalancesResponse>('/chat-balances'),
    staleTime: 15_000,
  });
}

/** Конфиг источника остатков (URL/токен/вкл). Только admin (chat-balances.manage). */
export function useChatBalancesConfig(enabled = true) {
  return useQuery({
    queryKey: ['chat-balances', 'config'] as const,
    queryFn: () => api.get<ChatBalancesConfigDto>('/chat-balances/config'),
    enabled,
  });
}

/** Аудит балансов: сверка чатов бота с проводками. Право audit.view. */
export function useBalanceAudit() {
  return useQuery({
    queryKey: queryKeys.balanceAudit,
    queryFn: () => api.get<BalanceAuditResponse>('/balance-audit'),
    staleTime: 15_000,
  });
}

/** Параметры GET /archive/findings (все, кроме status, необязательны). */
export type ArchiveFindingsParams = {
  status?: 'open' | 'resolved' | 'auto';
  kind?: FindingKind;
  excludeKind?: FindingKind;
  chatId?: string;
  limit?: number;
  /** false — не запрашивать (например, у пользователя нет права audit.view). */
  enabled?: boolean;
};

/**
 * Находки целостности архива (вкладка «Целостность» на /audit). Право audit.view.
 *
 * Пагинация КУРСОРНАЯ и бесконечная: одна страница врала бы счётчикам вкладки —
 * ежедневные `unlinked-op` (низкая серьёзность, ~9 в сутки) вытесняют настоящие
 * находки за границу limit'а, и раздел «Расхождения» показал бы «расхождений нет»
 * при живых расхождениях. Разделы тянутся независимо (`kind` / `excludeKind`),
 * чтобы шум одного не съедал страницу другого.
 */
export function useArchiveFindings(params: ArchiveFindingsParams = {}) {
  const { status = 'open', kind, excludeKind, chatId, limit = 100, enabled = true } = params;
  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.archiveFindings,
      status,
      kind ?? null,
      excludeKind ?? null,
      chatId ?? null,
      limit,
    ] as const,
    queryFn: ({ pageParam }) =>
      api.get<ArchiveFindingsResponse>('/archive/findings', {
        status,
        limit,
        ...(kind ? { kind } : {}),
        ...(excludeKind ? { excludeKind } : {}),
        ...(chatId ? { chatId } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 15_000,
    enabled,
  });
}

/**
 * Покрытие архивом по (чат, сутки, валюта) — раздел «Покрытие» вкладки «Целостность».
 *
 * `from`/`to` задавать ОБЯЗАТЕЛЬНО для UI: без окна сервер отдаёт всю таблицу
 * `archive.chat_coverage` (ретеншн 365 суток, гранулярность чат × сутки × валюта —
 * десятки тысяч строк), а годовое среднее покрытия не отреагирует на сегодняшний
 * провал, ради сигнала о котором карточка и существует.
 */
export function useArchiveCoverage(from?: string, to?: string) {
  return useQuery({
    queryKey: [...queryKeys.archiveCoverage, from ?? null, to ?? null] as const,
    queryFn: () =>
      api.get<ArchiveCoverageResponse>('/archive/coverage', {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }),
    staleTime: 15_000,
  });
}

/** Обогащение секции «Движения в боте» в AuditChatModal (C4): привязка к заявке/контрагенту. */
export function useArchiveOps(chatId: string | null, date?: string) {
  return useQuery({
    queryKey: chatId ? queryKeys.archiveOps(chatId, date) : ['archive', 'ops', '__none__'],
    queryFn: () => api.get<ArchiveOpsResponse>('/archive/ops', { chatId: chatId!, ...(date ? { date } : {}) }),
    enabled: !!chatId,
    staleTime: 15_000,
  });
}

/** Таймлайн дельты (chat, ccy) для графика в drill-down. */
export function useAuditHistory(
  chatId: string | null,
  ccy: string | null,
  range: '7d' | '30d' | '90d' | '365d' = '30d',
) {
  return useQuery({
    queryKey: ['balance-audit', 'history', chatId, ccy, range] as const,
    queryFn: () =>
      api.get<AuditHistoryResponse>('/balance-audit/history', { chatId: chatId!, ccy: ccy!, range }),
    enabled: !!chatId && !!ccy,
  });
}

/**
 * Журнал движений бота по чату — бесконечная offset-пагинация страницами
 * по `limit` (сервер cap 200). placeholderData сохраняет прошлые страницы
 * при смене даты — секция не мигает.
 */
export function useBotTransactions(chatId: string | null, date: string | null, limit = 50) {
  return useInfiniteQuery({
    queryKey: ['balance-audit', 'bot-txns', chatId, date, limit] as const,
    queryFn: ({ pageParam }) =>
      api.get<BotTxnsResponse>('/balance-audit/bot-transactions', {
        chatId: chatId!,
        ...(date ? { date } : {}),
        limit,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage.configured || lastPage.error) return undefined;
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: !!chatId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Лента изменений балансов (/balances «Журнал изменений») — бесконечная
 * курсорная пагинация по id (before = id последней строки страницы).
 * refetch 60с — новые изменения подтягиваются без ручного обновления.
 *
 * `window` — опциональное окно `[from, to]` (ISO datetime) по `occurredAt`,
 * для `DivergenceModal` (список операций за интервал точки графика с
 * расхождением). Без него — прежнее поведение (вся лента, без фильтра
 * по времени), существующие вызовы (`BalanceChangesLog`) не меняются.
 *
 * ВАЖНО: api.get сам разворачивает серверный конверт {data: ...} (см. client.ts:exec),
 * поэтому дженерик — сразу BalanceChangesResponse, без .then((r) => r.data).
 */
export function useBalanceChanges(limit = 50, win?: { from: string; to: string }) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.balanceChanges, limit, win?.from ?? null, win?.to ?? null] as const,
    queryFn: ({ pageParam }) =>
      api.get<BalanceChangesResponse>('/balance-changes', {
        limit,
        ...(pageParam ? { before: pageParam } : {}),
        ...(win ? { from: win.from, to: win.to } : {}),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.items[lastPage.items.length - 1]?.id : undefined,
    refetchInterval: 60_000,
  });
}

/**
 * Непарные движения журнала за окно — подтверждение расхождений позиции
 * (`confirmDivergences` в `components/balances/trendMath.ts`). `from`/`to` —
 * окно ПО КАНДИДАТАМ (см. `divergenceMatchWindow`), не по всему графику:
 * `null` (нет кандидатов) выключает запрос через `enabled`.
 */
export function useUnmatched(from: string | null, to: string | null, minUsdt = 2000) {
  return useQuery({
    queryKey: [...queryKeys.balanceChanges, 'unmatched', from, to, minUsdt] as const,
    queryFn: () => api.get<UnmatchedResponse>('/balance-changes/unmatched', { from: from!, to: to!, minUsdt }),
    enabled: !!from && !!to,
    staleTime: 60_000,
  });
}

export type TransactionsData = { items: TransactionRow[]; total: number };

export function useTransactions(page = 0, pageSize = 100) {
  return useQuery({
    queryKey: queryKeys.transactions(page),
    queryFn: () => api.get<TransactionsData>('/transactions', { limit: pageSize, offset: page * pageSize }),
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get<DashboardResponse>('/dashboard'),
  });
}

// ─── rate-providers ───

export function useRateProviders() {
  return useQuery({
    queryKey: ['ref', 'usdt-rates', 'providers'] as const,
    queryFn: () => api.get<RateProviderInfo[]>('/ref/usdt-rates/providers'),
    staleTime: Infinity, // реестр статичен в рамках сессии
  });
}

export function useRateSources(ccy: CcyCode | null) {
  return useQuery({
    queryKey: ['ref', 'usdt-rates', 'sources', ccy] as const,
    queryFn: () => api.get<RateSourceConfig[]>(`/ref/usdt-rates/${ccy}/sources`),
    enabled: !!ccy,
  });
}

export function useRateHistory(ccy: CcyCode | null, limit = 100) {
  return useQuery({
    queryKey: ['ref', 'usdt-rates', 'history', ccy, limit] as const,
    queryFn: () => api.get<RateHistoryEntry[]>(`/ref/usdt-rates/${ccy}/history`, { limit }),
    enabled: !!ccy,
  });
}

// ─── telegram-listener ───

export function useTelegramHandlers() {
  return useQuery({
    queryKey: ['telegram', 'handlers'] as const,
    queryFn: () => api.get<TelegramHandlerInfo[]>('/telegram/handlers'),
    staleTime: Infinity, // статичный реестр
  });
}

export function useTelegramChats() {
  return useQuery({
    queryKey: ['telegram', 'chats'] as const,
    queryFn: () => api.get<TelegramListeningChat[]>('/telegram/chats'),
  });
}

/**
 * Мультивалютные балансы чата, распарсенные из сообщений действием
 * «update-chat-balance». Запрашивается только при развёрнутой панели
 * (enabled по chatId); refetchInterval — per-observer, обновление идёт
 * пока панель смонтирована.
 */
export function useTelegramChatBalances(chatId: string | null) {
  return useQuery({
    queryKey: ['telegram', 'chat-balances', chatId] as const,
    queryFn: () => api.get<TelegramChatBalance[]>(`/telegram/chats/${chatId}/balances`),
    enabled: !!chatId,
    refetchInterval: 30_000,
  });
}

export function useChatSyncConfig() {
  return useQuery({
    queryKey: ['telegram', 'chat-sync', 'config'] as const,
    queryFn: () => api.get<ChatSyncConfigDto>('/telegram/chat-sync/config'),
  });
}

export function useTelegramRules() {
  return useQuery({
    queryKey: ['telegram', 'rules'] as const,
    queryFn: () => api.get<TelegramRule[]>('/telegram/rules'),
  });
}

export function useTelegramEvents(status?: TelegramEventStatus, limit = 100) {
  return useQuery({
    queryKey: ['telegram', 'events', status ?? 'all', limit] as const,
    queryFn: () =>
      api.get<TelegramEvent[]>('/telegram/events', { ...(status ? { status } : {}), limit }),
    refetchInterval: 15_000, // события приходят от бота фоном — поллим пока страница открыта
  });
}

export function useTelegramPendingCount() {
  return useQuery({
    queryKey: ['telegram', 'events', 'count-pending'] as const,
    queryFn: () => api.get<{ count: number }>('/telegram/events/count-pending'),
    refetchInterval: 30_000, // polling каждые 30 секунд для бейджа
  });
}

/**
 * Сообщения из message_log (вкладка «Сообщения», примеры в редакторе правил).
 * `opts.refetchInterval` — автообновление, пока компонент-подписчик смонтирован.
 */
export function useTelegramMessages(
  chatId: string | null,
  limit = 30,
  opts?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: ['telegram', 'messages', chatId, limit] as const,
    queryFn: () =>
      api.get<TelegramMessageLogEntry[]>('/telegram/messages', { chatId: chatId!, limit }),
    enabled: !!chatId,
    refetchInterval: opts?.refetchInterval,
  });
}

/** Статус бота: подключение, режим + runtime-состояние листенера (нужен ли рестарт). */
export function useTelegramBotStatus() {
  return useQuery({
    queryKey: ['telegram', 'bot', 'status'] as const,
    queryFn: () => api.get<TelegramBotStatusDto>('/telegram/bot/status'),
    refetchInterval: 60_000,
  });
}

/** Конфиг бота из БД (токен/режим/webhook). Токен только в виде маски. */
export function useTelegramBotConfig() {
  return useQuery({
    queryKey: ['telegram', 'bot', 'config'] as const,
    queryFn: () => api.get<TelegramBotConfigDto>('/telegram/bot/config'),
  });
}

/** Библиотека pre-built templates правил. */
export function useTelegramTemplates() {
  return useQuery({
    queryKey: ['telegram', 'templates'] as const,
    queryFn: () => api.get<RuleTemplate[]>('/telegram/templates'),
    staleTime: 10 * 60 * 1000,
  });
}

// ─── tron-wallets ───

/**
 * Конфиг опроса TRON-кошельков через TronScan (ключ шифрован на бэке — сюда
 * приходит только флаг `keySet`). Доступно admin (chat-balances.manage) —
 * `enabled` передавать флагом, звать только при открытой модалке/canManage.
 */
export function useTronConfig(enabled = true) {
  return useQuery({
    queryKey: queryKeys.tronConfig,
    queryFn: () => api.get<TronConfigDto>('/tron-wallets/config'),
    enabled,
  });
}

/** Отслеживаемые TRON-кошельки + последний закэшированный баланс USDT. */
export function useTronWallets(enabled = true) {
  return useQuery({
    queryKey: queryKeys.tronWallets,
    queryFn: () => api.get<TronWalletRow[]>('/tron-wallets'),
    enabled,
  });
}
